import React, { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { resolveScopedOverride } from '@vibesites/runtime';
import {
  EditableToken,
  RichTextContent,
  buildScopeContext,
  useCollectionBinding,
  useCollectionBindingOverrides,
  useContentToken,
  usePage,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import {
  hasCustomAmenityOrder,
  sortAmenities,
  toTitleCaseAmenity,
} from '../utils/sortAmenities';

interface Amenity {
  title?: string | null;
  description?: string | null;
  group?: string | null;
  amenity_icon?: string | null;
  amenity_image?: string | null;
  amenity_image_alt_text?: string | null;
  is_featured?: string | null;
  [key: string]: unknown;
}

interface GalleryPhoto {
  image?: string | null;
  title?: string | null;
  description?: string | null;
  alt_text?: string | null;
  [key: string]: unknown;
}

// ============================================================================
// Photo slider
// ============================================================================

const SLIDE_INTERVAL = 5000;

interface PhotoSliderProps {
  photos: GalleryPhoto[];
  heading: string;
}

function PhotoSlider({ photos, heading }: PhotoSliderProps) {
  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const count = photos.length;

  const advance = useCallback((dir: 1 | -1) => {
    setCurrent(prev => (prev + dir + count) % count);
  }, [count]);

  useEffect(() => {
    if (count <= 1 || paused) return;
    timerRef.current = setInterval(() => advance(1), SLIDE_INTERVAL);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [count, paused, advance]);

  if (count === 0) return null;

  return (
    <div
      className="amenities-slider"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {photos.map((photo, i) => (
        <VibesitesImage
          key={photo.image || i}
          url={photo.image!}
          alt={photo.alt_text || photo.title || heading}
          className={`amenities-slider-img${i === current ? ' amenities-slider-img--active' : ''}`}
          sizes="(min-width: 768px) 50vw, 100vw"
          priority={i === 0}
        />
      ))}

      {count > 1 && (
        <>
          <button
            className="amenities-slider-arrow amenities-slider-arrow--prev"
            onClick={() => advance(-1)}
            aria-label="Previous photo"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
          </button>
          <button
            className="amenities-slider-arrow amenities-slider-arrow--next"
            onClick={() => advance(1)}
            aria-label="Next photo"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
          </button>

          <div className="amenities-slider-dots">
            {photos.map((_, i) => (
              <button
                key={i}
                className={`amenities-slider-dot${i === current ? ' amenities-slider-dot--active' : ''}`}
                onClick={() => setCurrent(i)}
                aria-label={`Photo ${i + 1}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ============================================================================
// Column component
// ============================================================================

/** Switch to two-column layout within a card once the list would feel tall. */
const DUAL_COLUMN_THRESHOLD = 8;

interface AmenityColumnProps {
  headingNode: ReactNode;
  headingText: string;
  items: Amenity[];
  photos: GalleryPhoto[];
  disclaimer?: ReactNode;
}

function AmenityColumn({ headingNode, headingText, items, photos, disclaimer }: AmenityColumnProps) {
  const colRef = useScrollReveal<HTMLDivElement>();
  const useDualColumns = items.length > DUAL_COLUMN_THRESHOLD;

  return (
    <div ref={colRef as React.RefObject<HTMLDivElement>} className="reveal-fade-up amenities-col">
      {/* Photo slider */}
      {photos.length > 0 && <PhotoSlider photos={photos} heading={headingText} />}

      {/* Heading */}
      {headingNode}

      {/* Items — all visible; dual columns when the list is long */}
      <ul
        className={`amenities-col-list${useDualColumns ? ' amenities-col-list--dual' : ''}`}
      >
        {items.map((item, i) => (
          <li key={item.title ? `${item.title}-${i}` : i} className="amenities-col-item">
            <span className="amenities-col-bullet" aria-hidden="true" />
            <span>{item.title ? toTitleCaseAmenity(item.title) : ''}</span>
          </li>
        ))}
      </ul>

      {disclaimer}
    </div>
  );
}

// ============================================================================
// Main component
// ============================================================================

export function AmenitiesList() {
  const communityHeading = useContentToken('amenitieslist.communityHeading');
  const apartmentHeading = useContentToken('amenitieslist.apartmentHeading');
  const page = usePage();
  const scopeCtx = buildScopeContext(page);
  const collectionOverrides = useCollectionBindingOverrides();

  const communityCustomOrder = hasCustomAmenityOrder(
    resolveScopedOverride(collectionOverrides, 'amenitieslist.communityItems', scopeCtx),
  );
  const apartmentCustomOrder = hasCustomAmenityOrder(
    resolveScopedOverride(collectionOverrides, 'amenitieslist.apartmentItems', scopeCtx),
  );

  const communityItemsRaw = useCollectionBinding<Amenity>('amenitieslist.communityItems', {
    collection: 'amenities',
    filter: { group: 'Community' },
  });
  const apartmentItemsRaw = useCollectionBinding<Amenity>('amenitieslist.apartmentItems', {
    collection: 'amenities',
    filter: { group: 'Apartment' },
  });

  // Featured / desirability sort by default; editor settings reorder wins when present.
  const communityItems = useMemo(
    () => sortAmenities(communityItemsRaw, { customOrder: communityCustomOrder }),
    [communityItemsRaw, communityCustomOrder],
  );
  const apartmentItems = useMemo(
    () => sortAmenities(apartmentItemsRaw, { customOrder: apartmentCustomOrder }),
    [apartmentItemsRaw, apartmentCustomOrder],
  );

  const showApartmentSelectHomesDisclaimer = useMemo(
    () => apartmentItems.some((item) => item.title?.includes('*')),
    [apartmentItems],
  );
  const apartmentSelectHomesDisclaimer = useContentToken('amenitieslist.apartmentSelectHomesDisclaimer');

  const communityPhotos = useCollectionBinding<GalleryPhoto>('amenitieslist.communityPhotos', {
    collection: 'photo_gallery',
    filter: { title: 'Community' },
    defaultLimit: 8,
  });

  const apartmentPhotos = useCollectionBinding<GalleryPhoto>('amenitieslist.apartmentPhotos', {
    collection: 'photo_gallery',
    filter: { title: 'Apartment' },
    defaultLimit: 8,
  });

  useRevealStyles();

  const hasContent = communityItems.length > 0 || apartmentItems.length > 0;

  return (
    <section className="py-8 md:py-12 bg-[var(--vibe-color-background)]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {hasContent ? (
          <div className="amenities-grid">
            {apartmentItems.length > 0 && (
              <AmenityColumn
                headingNode={
                  <RichTextContent
                    path="amenitieslist.apartmentHeading"
                    as="h2"
                    className="amenities-col-heading"
                  />
                }
                headingText={(apartmentHeading || 'Apartment Features').replace(/<[^>]*>/g, '')}
                items={apartmentItems}
                photos={apartmentPhotos}
                disclaimer={
                  showApartmentSelectHomesDisclaimer ? (
                    <EditableToken
                      path="amenitieslist.apartmentSelectHomesDisclaimer"
                      as="p"
                      className="amenities-col-disclaimer"
                    >
                      {apartmentSelectHomesDisclaimer || '* Available in select homes'}
                    </EditableToken>
                  ) : undefined
                }
              />
            )}
            {communityItems.length > 0 && (
              <AmenityColumn
                headingNode={
                  <RichTextContent
                    path="amenitieslist.communityHeading"
                    as="h2"
                    className="amenities-col-heading"
                  />
                }
                headingText={(communityHeading || 'Community Amenities').replace(/<[^>]*>/g, '')}
                items={communityItems}
                photos={communityPhotos}
              />
            )}
          </div>
        ) : (
          <div className="text-center py-12 text-[var(--vibe-color-text-muted)]">
            <p className="font-body text-lg">Amenities will be displayed here.</p>
          </div>
        )}
      </div>

      <style>{`
        .amenities-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 2rem;
        }
        @media (max-width: 768px) {
          .amenities-grid {
            grid-template-columns: 1fr;
          }
        }

        .amenities-col {
          background: white;
          border: 1px solid var(--vibe-color-border);
          overflow: hidden;
          display: flex;
          flex-direction: column;
        }

        /* Photo slider */
        .amenities-slider {
          position: relative;
          width: 100%;
          height: 300px;
          overflow: hidden;
          flex-shrink: 0;
        }
        .amenities-slider-img {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
          opacity: 0;
          transition: opacity 0.7s cubic-bezier(0.22, 1, 0.36, 1);
        }
        .amenities-slider-img--active {
          opacity: 1;
        }

        .amenities-slider-arrow {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          width: 32px;
          height: 32px;
          border-radius: 50%;
          border: none;
          background: rgba(0, 0, 0, 0.35);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          opacity: 0;
          transition: opacity 0.3s, background 0.3s;
          z-index: 2;
        }
        .amenities-slider:hover .amenities-slider-arrow {
          opacity: 1;
        }
        .amenities-slider-arrow:hover {
          background: rgba(0, 0, 0, 0.55);
        }
        .amenities-slider-arrow--prev { left: 10px; }
        .amenities-slider-arrow--next { right: 10px; }

        .amenities-slider-dots {
          position: absolute;
          bottom: 10px;
          left: 50%;
          transform: translateX(-50%);
          display: flex;
          gap: 6px;
          z-index: 2;
        }
        .amenities-slider-dot {
          width: 7px;
          height: 7px;
          border-radius: 50%;
          border: 1.5px solid rgba(255, 255, 255, 0.7);
          background: transparent;
          padding: 0;
          cursor: pointer;
          transition: background 0.25s, border-color 0.25s, transform 0.25s;
        }
        .amenities-slider-dot:hover {
          transform: scale(1.25);
          border-color: white;
        }
        .amenities-slider-dot--active {
          background: white;
          border-color: white;
        }

        @media (prefers-reduced-motion: reduce) {
          .amenities-slider-img {
            transition: none;
          }
        }

        .amenities-col-heading {
          font-family: var(--vibe-font-heading);
          font-size: 1.35rem;
          font-weight: 400;
          color: var(--vibe-color-text);
          text-align: center;
          padding: 1.25rem 1.5rem 0;
          margin: 0;
          letter-spacing: 0.02em;
        }

        .amenities-col-list {
          list-style: none;
          padding: 1rem 1.75rem 1.5rem;
          margin: 0;
          columns: 1;
          column-gap: 1.5rem;
          flex: 1;
        }
        @media (min-width: 480px) {
          .amenities-col-list--dual {
            columns: 2;
          }
        }

        .amenities-col-item {
          display: flex;
          align-items: center;
          gap: 0.6rem;
          padding: 0.3rem 0;
          font-family: var(--vibe-font-body);
          font-size: 0.88rem;
          color: var(--vibe-color-text);
          break-inside: avoid;
          line-height: 1.5;
        }

        .amenities-col-bullet {
          flex-shrink: 0;
          width: 5px;
          height: 5px;
          border-radius: 50%;
          background: var(--vibe-color-primary);
        }

        .amenities-col-disclaimer {
          margin: 0;
          padding: 0 1.75rem 1.25rem;
          font-family: var(--vibe-font-body);
          font-size: 0.8rem;
          line-height: 1.45;
          color: var(--vibe-color-text-muted);
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'AmenitiesList',
  displayName: 'Amenities List',
  tokens: {
    'amenitieslist.communityHeading': {
      label: 'Community Heading',
      type: 'richtext',
      defaultValue: '<p>Community Amenities</p>',
    },
    'amenitieslist.apartmentHeading': {
      label: 'Apartment Heading',
      type: 'richtext',
      defaultValue: '<p>Apartment Features</p>',
    },
    'amenitieslist.apartmentSelectHomesDisclaimer': {
      label: 'Apartment Select Homes Disclaimer',
      type: 'text',
      defaultValue: '* Available in select homes',
    },
  },
  collectionBindings: {
    'amenitieslist.communityItems': {
      label: 'Community Amenities',
      collection: 'amenities',
    },
    'amenitieslist.apartmentItems': {
      label: 'Apartment Features',
      collection: 'amenities',
    },
    'amenitieslist.communityPhotos': {
      label: 'Community Photos',
      collection: 'photo_gallery',
    },
    'amenitieslist.apartmentPhotos': {
      label: 'Apartment Photos',
      collection: 'photo_gallery',
    },
  },
};

(AmenitiesList as any).contentSchema = contentSchema;

export default AmenitiesList;
